import { Link, useLocation } from "react-router";

import { Logo } from "@/components/common/logo";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import type { NavItem } from "@/lib/nav";

/**
 * Portal-agnostic sidebar. Navigation comes entirely from the `items` config,
 * so new modules never require layout changes.
 */
export function AppSidebar({
  items,
  homeUrl,
  groupLabel = "Portal",
  footerTitle,
  footerSubtitle,
}: {
  items: NavItem[];
  homeUrl: string;
  groupLabel?: string | undefined;
  footerTitle: string;
  footerSubtitle?: string | undefined;
}) {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const { pathname } = useLocation();

  return (
    <Sidebar collapsible="icon" className="border-r border-sidebar-border">
      <SidebarHeader className="h-16 justify-center px-3">
        <Link to={homeUrl} className="flex items-center">
          <Logo showWordmark={!collapsed} />
        </Link>
      </SidebarHeader>

      <SidebarContent className="px-2">
        <SidebarGroup>
          {!collapsed && (
            <SidebarGroupLabel className="text-[0.7rem] tracking-widest uppercase">
              {groupLabel}
            </SidebarGroupLabel>
          )}
          <SidebarGroupContent>
            <SidebarMenu>
              {items.map((item) => (
                <SidebarMenuItem key={item.url}>
                  <SidebarMenuButton
                    asChild
                    isActive={pathname === item.url}
                    tooltip={item.title}
                    className="h-10 rounded-xl transition-colors"
                  >
                    <Link to={item.url} className="flex items-center gap-3">
                      <item.icon className="size-4 shrink-0" />
                      {!collapsed && <span className="font-medium">{item.title}</span>}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {!collapsed && (
        <SidebarFooter className="px-4 pb-5">
          <p className="text-xs leading-relaxed text-muted-foreground">
            {footerTitle}
            {footerSubtitle && (
              <>
                <br />
                <span className="opacity-70">{footerSubtitle}</span>
              </>
            )}
          </p>
        </SidebarFooter>
      )}
    </Sidebar>
  );
}
